/* External */
import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

/* Components */
import PlayResumeButton from '../PlayResumeButton/PlayResumeButton';
import WatchFromStartButton from '../WatchFromStartButton';

/* Utils */
import { PLAY_SOURCE_PAGE } from '../../utils/trackSourceConviva';
import useRedirectToPlayer from '../../hooks/useRedirectToPlayer';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';
import { useFullEpisodeNumber } from '../../utils/metaDataUtils';
import { noOp } from '../../utils/functions';
import { GTM_EVENT_NAMES } from '../../utils/googleTags';

/* Constants */
const buttonTextsId = {
  startWatching: 'startWatching',
  resume: 'resume',
  nextEpisode: 'nextEpisode',
  watchNow: 'watchNow',
};

function ContinueWatchingShowButton({
  focusKey,
  className,
  episodeId,
  episodeContent,
  firstEpisode,
  focusRestorable,
  onBecameFocused,
  locationState,
  pdpCommonTagEvent,
  blockDownArrow,
  blockUpArrow,
}) {
  const { formatMessage } = useIntl();
  const episode = episodeId ? episodeContent : firstEpisode;

  const {
    title,
    duration,
    progress,
    episode: episodeProps,
  } = episode || {};
  const { showName } = episodeProps || {};

  const isNonEntitledContent = useLockIcon(episode);
  const isContentBlockedByParentalControl = parentalControlBlock(episode);

  const progressOffset = useMemo(() => {
    if (progress >= duration * 0.95) {
      return 0;
    }

    return progress;
  }, [duration, progress]);

  const buttonTextId = useMemo(() => {
    if (episodeId) {
      if (progressOffset && progressOffset > 0) {
        return buttonTextsId.resume;
      }
      return buttonTextsId.nextEpisode;
    }
    return buttonTextsId.startWatching;
  }, [episodeId, progressOffset]);

  const seasonAndEpisode = useFullEpisodeNumber(episode);

  const handleRedirectToPlayer = useRedirectToPlayer(episode, {
    page: PLAY_SOURCE_PAGE.PDP,
    section: showName || title || '',
    state: locationState,
  });

  const handleWatchFromStart = useRedirectToPlayer(episode, {
    page: PLAY_SOURCE_PAGE.PDP,
    section: showName || title || '',
    query: { watchFromStart: true },
    state: locationState,
  });

  const handleEnterPress = useCallback(() => {
    if (!episode?.vrioAssetId) return;
    handleRedirectToPlayer();
    pdpCommonTagEvent({
      eventName: buttonTextId === 'resume' ? GTM_EVENT_NAMES.PDP.RESUME : GTM_EVENT_NAMES.PDP.WATCH,
    });
  }, [buttonTextId, handleRedirectToPlayer, pdpCommonTagEvent, episode?.vrioAssetId]);

  const handleEnterPressWatchFromStart = useCallback(() => {
    handleWatchFromStart();
    pdpCommonTagEvent({ eventName: GTM_EVENT_NAMES.PDP.RESTART });
  }, [handleWatchFromStart, pdpCommonTagEvent]);

  const formatMessageEditing = useMemo(() => {
    if (isNonEntitledContent || isContentBlockedByParentalControl) {
      return `${formatMessage({ id: 'common.PlayButtonLockIcon' })}`;
    }

    const buttonText = formatMessage({ id: `common.${buttonTextId}` });
    return `${buttonText}: ${seasonAndEpisode}`;
  }, [
    isNonEntitledContent,
    isContentBlockedByParentalControl,
    buttonTextId,
    seasonAndEpisode,
    formatMessage,
  ]);

  return (
    <>
      <PlayResumeButton
        className={className}
        focusKey={focusKey}
        primary
        progress={episodeId ? progressOffset : 0}
        duration={duration}
        focusRestorable={focusRestorable}
        isNonEntitledContent={isNonEntitledContent}
        isContentBlockedByParentalControl={isContentBlockedByParentalControl}
        onBecameFocused={onBecameFocused}
        onPress={handleEnterPress}
        blockUpArrow={blockUpArrow}
        blockDownArrow={blockDownArrow}
      >
        {formatMessageEditing}
      </PlayResumeButton>
      {episodeId
        && progressOffset > 0
        && <WatchFromStartButton onPress={handleEnterPressWatchFromStart} />}
    </>
  );
}

ContinueWatchingShowButton.propTypes = {
  focusKey: PropTypes.string,
  className: PropTypes.string,
  episodeId: PropTypes.string,
  episodeContent: PropTypes.instanceOf(Object),
  firstEpisode: PropTypes.instanceOf(Object),
  focusRestorable: PropTypes.bool,
  onBecameFocused: PropTypes.func,
  locationState: PropTypes.instanceOf(Object),
  pdpCommonTagEvent: PropTypes.func,
  blockDownArrow: PropTypes.bool,
  blockUpArrow: PropTypes.bool,
};

ContinueWatchingShowButton.defaultProps = {
  focusKey: undefined,
  className: '',
  episodeId: '',
  episodeContent: undefined,
  firstEpisode: undefined,
  focusRestorable: undefined,
  onBecameFocused: undefined,
  locationState: {},
  pdpCommonTagEvent: noOp,
  blockDownArrow: false,
  blockUpArrow: true,
};

export default memo(ContinueWatchingShowButton);
